import React from "react";
import classnames from "classnames";
import {AutoComplete, Checkbox, DatePicker, Form, Input, Select, Switch, Tooltip} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {FormInputV2} from "./form";
import {AutoCompleteAddressFormInput} from "../utils/AutoCompleteAddress";
import "./FormModal/form-modal.css";
import locale from "antd/lib/locale/he_IL";
import {CustomTimePicker} from "./CustomTimePicker.jsx";
import {ProgressiveImageLoader} from "./ProgressiveImageLoader.jsx";

const {Option} = Select;
const {TextArea} = Input;

export const getInputComponent = input => {
    let component;

    switch (input.type) {
        case "custom":
            component = (
                <Form.Item name={input.name} rules={input.rules ?? []}>
                    {input.customComponent}
                </Form.Item>
            );
            break;
        case "address":
            component = (
                <AutoCompleteAddressFormInput
                    placeholder={input?.placeholder}
                    allowSelectRegularText={true}
                    editValue={input.value}
                    disabled={input.disabled}
                    countriesToSearchIn={["IL"]}
                    locationTypes={[]}
                />
            );
            break;
        case "boolean":
            component = (
                <Form.Item name={input.name} valuePropName={"checked"}>
                    <Switch
                        disabled={input.disabled}
                        checkedChildren={input.checkedChildren}
                        unCheckedChildren={input.unCheckedChildren}
                    />
                </Form.Item>
            );
            break;
        case "checkbox":
            component = (
                <Form.Item
                    name={input.name}
                    valuePropName="checked"
                    rules={[
                        ...(!input.optional
                            ? [
                                  {
                                      required: true,
                                      validator: (_, checked) => (checked ? Promise.resolve() : Promise.reject())
                                  }
                              ]
                            : [])
                    ]}>
                    <Checkbox className="form-input-component-checkbox" disabled={input.disabled}>
                        {input.checkboxText}
                    </Checkbox>
                </Form.Item>
            );
            break;
        case "select":
            component = (
                <Select
                    listHeight={input?.listHeight}
                    optionFilterProp={input?.optionFilterProp}
                    showSearch={input?.showSearch}
                    id={input.id}
                    popupClassName={input.popupClassName}
                    loading={input.loading}
                    disabled={input.disabled}
                    optionLabelProp={input.optionLabelProp}
                    mode={input.multiple ? "multiple" : ""}
                    placeholder={input.placeholder}>
                    {input.options?.map(option => (
                        <Option key={option.name} value={option.name} label={option.label} disabled={option.disabled}>
                            {!!option?.imageUrl && (
                                <div className="input-component-select-image-container">
                                    <ProgressiveImageLoader width={30} height={30} src={option.imageUrl} />
                                </div>
                            )}

                            {option.label}

                            {option.tag ? (
                                <span className="provider-kosher-badge">{option.tag}</span>
                            ) : null}

                            {option.info ? (
                                <Tooltip title={option.info}>
                                    <QuestionCircleOutlined className="form-modal-input-info" />
                                </Tooltip>
                            ) : null}
                        </Option>
                    ))}
                </Select>
            );
            break;
        case "autocomplete":
            component = (
                <AutoComplete disabled={input.disabled} placeholder={input.placeholder}>
                    {input.options?.map(option => (
                        <Option key={option.name} value={option.name}>
                            {option.label}
                        </Option>
                    ))}
                </AutoComplete>
            );
            break;
        case "multi":
            component = (
                <div className={classnames("form-modal-multiple-input-line", input.className)}>
                    {input.inputs.map(subInput => (
                        <div
                            className={classnames("form-modal-multiple-input-sub-input", subInput.className)}
                            key={subInput.name}>
                            {subInput.label ? (
                                <label>
                                    <span className="form-modal-input-label">{subInput.label}</span>
                                    {subInput.info ? (
                                        <Tooltip title={subInput.info}>
                                            <QuestionCircleOutlined
                                                disabled={input.disabled}
                                                className="form-modal-input-info"
                                            />
                                        </Tooltip>
                                    ) : null}
                                </label>
                            ) : null}
                            {getInputComponent(subInput)}
                        </div>
                    ))}
                </div>
            );
            break;
        case "textarea":
            component = (
                <TextArea
                    name={input.name}
                    autoSize={!input.rows ? {minRows: input.minRows, maxRows: input.maxRows} : undefined}
                    maxLength={input.maxLength}
                    placeholder={input.placeholder}
                    disabled={input.disabled}
                    rows={input.rows}
                />
            );
            break;
        case "date":
            component = (
                <DatePicker
                    inputReadOnly={true}
                    style={{
                        width: "100%",
                        height: "40px",
                        fontSize: "14px",
                        borderRadius: "5px"
                    }}
                    format={input.format ?? "DD MMM, YYYY"}
                    placeholder={input.placeholder}
                    disabledDate={input?.disabledDate}
                    disabled={input.disabled}
                    renderExtraFooter={input.renderExtraFooter}
                />
            );
            break;
        case "time":
            component = (
                <CustomTimePicker
                    style={{
                        width: "100%",
                        height: "40px",
                        fontSize: "14px",
                        borderRadius: "5px"
                    }}
                    locale={locale}
                    format="HH:mm"
                    suffixIcon={null}
                    minuteStep={15}
                    inputReadOnly={false}
                    placeholder={input?.placeholder ?? "משעה"}
                    showNow={true}
                    disabled={input.disabled}
                />
            );
            break;
        default:
            component = (
                <FormInputV2
                    className={input.className}
                    disabled={input.disabled}
                    type={input.type}
                    placeholder={input.placeholder}
                    onWheel={e => e.target.blur()}
                />
            );
            break;
    }

    return !["multi", "custom", "boolean", "checkbox"].includes(input.type) ? (
        <Form.Item
            className={input.formItemClassName}
            name={input.name}
            rules={[
                ...(!input.optional
                    ? [
                          {
                              required: true,
                              validator: (_, result) => {
                                  if (!result && result !== 0) {
                                      return Promise.reject("שדה חובה");
                                  }
                                  return Promise.resolve();
                              }
                          }
                      ]
                    : []),
                ...(input.rules ?? [])
            ]}>
            {component}
        </Form.Item>
    ) : (
        component
    );
};

export const renderInputLabel = input => {
    if (!input.label) {
        return <></>;
    }

    return (
        <label>
            {input.label}
            {input.info ? (
                <Tooltip title={input.info}>
                    <QuestionCircleOutlined className="form-modal-input-info" />
                </Tooltip>
            ) : null}
        </label>
    );
};
